const TILE_SIZE = 256;
const EARTH_METERS_PER_PIXEL_AT_EQUATOR = 156543.03392;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const lngToWorldX = (lng, zoom) => (
  ((lng + 180) / 360) * TILE_SIZE * Math.pow(2, zoom)
);

const latToWorldY = (lat, zoom) => {
  const clampedLat = clamp(lat, -85.05112878, 85.05112878);
  const sinLat = Math.sin((clampedLat * Math.PI) / 180);
  return (
    (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) *
    TILE_SIZE *
    Math.pow(2, zoom)
  );
};

const worldXToLng = (x, zoom) => (
  (x / (TILE_SIZE * Math.pow(2, zoom))) * 360 - 180
);

const worldYToLat = (y, zoom) => {
  const n = Math.PI - (2 * Math.PI * y) / (TILE_SIZE * Math.pow(2, zoom));
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
};

const getBboxArea = (bbox) => {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const centerLat = (minLat + maxLat) / 2;
  return (
    Math.abs(maxLon - minLon) *
    Math.abs(maxLat - minLat) *
    Math.max(0.2, Math.cos((centerLat * Math.PI) / 180))
  );
};

export const getLargestBbox = (captures) => {
  if (!Array.isArray(captures) || captures.length === 0) {
    return null;
  }

  return captures.reduce((largestBbox, [, , bbox]) => {
    if (!Array.isArray(bbox) || bbox.length < 4) {
      return largestBbox;
    }

    if (!largestBbox || getBboxArea(bbox) > getBboxArea(largestBbox)) {
      return bbox;
    }

    return largestBbox;
  }, null);
};

export const fitBboxToMapView = (
  bbox,
  viewport,
  {
    leftPadding = 280,
    rightPadding = 600,
    topPadding = 120,
    bottomPadding = 110,
    minZoom = 2,
    maxZoom = 10.5,
  } = {}
) => {
  if (!Array.isArray(bbox) || bbox.length < 4 || !viewport?.width || !viewport?.height) {
    return null;
  }

  const [minLon, minLat, maxLon, maxLat] = bbox.map(Number);
  if (![minLon, minLat, maxLon, maxLat].every(Number.isFinite)) {
    return null;
  }

  const availableWidth = Math.max(240, viewport.width - leftPadding - rightPadding);
  const availableHeight = Math.max(220, viewport.height - topPadding - bottomPadding);
  const centerLat = (minLat + maxLat) / 2;
  const centerLon = (minLon + maxLon) / 2;

  const widthAtZoomZero = Math.max(
    0.000001,
    Math.abs(lngToWorldX(maxLon, 0) - lngToWorldX(minLon, 0))
  );
  const heightAtZoomZero = Math.max(
    0.000001,
    Math.abs(latToWorldY(minLat, 0) - latToWorldY(maxLat, 0))
  );

  const zoomForWidth = Math.log2(availableWidth / widthAtZoomZero);
  const zoomForHeight = Math.log2(availableHeight / heightAtZoomZero);
  const zoom = clamp(Math.min(zoomForWidth, zoomForHeight), minZoom, maxZoom);

  const availableCenterX = leftPadding + availableWidth / 2;
  const availableCenterY = topPadding + availableHeight / 2;
  const offsetX = availableCenterX - viewport.width / 2;
  const offsetY = availableCenterY - viewport.height / 2;

  const bboxCenterWorldX = lngToWorldX(centerLon, zoom);
  const bboxCenterWorldY = latToWorldY(centerLat, zoom);
  const mapCenterWorldX = bboxCenterWorldX - offsetX;
  const mapCenterWorldY = bboxCenterWorldY - offsetY;

  return {
    center: [
      clamp(worldYToLat(mapCenterWorldY, zoom), -85, 85),
      clamp(worldXToLng(mapCenterWorldX, zoom), -180, 180),
    ],
    zoom,
  };
};

const getNiceScaleMeters = (meters) => {
  if (!Number.isFinite(meters) || meters <= 0) {
    return 0;
  }

  const exponent = Math.floor(Math.log10(meters));
  const magnitude = Math.pow(10, exponent);
  const normalized = meters / magnitude;

  if (normalized >= 5) {
    return 5 * magnitude;
  }

  if (normalized >= 2) {
    return 2 * magnitude;
  }

  return magnitude;
};

export const getScaleBarMeasurement = (lat, zoom, targetWidthPx = 96) => {
  const metersPerPixel =
    (EARTH_METERS_PER_PIXEL_AT_EQUATOR *
      Math.cos((clamp(Number(lat) || 0, -85, 85) * Math.PI) / 180)) /
    Math.pow(2, zoom);
  const niceMeters = getNiceScaleMeters(metersPerPixel * targetWidthPx);
  const widthPx = niceMeters > 0 ? niceMeters / metersPerPixel : 0;

  return {
    widthPx: clamp(widthPx, 32, targetWidthPx),
    label:
      niceMeters >= 1000
        ? `${Number((niceMeters / 1000).toFixed(2)).toString()} km`
        : `${Math.round(niceMeters)} m`,
  };
};
